// components/auth/Login.js
import React, { useState, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';
import { login, clearError, resendVerificationEmail, clearResendState } from '../../slices/authSlice.mjs';
import { setHeaderData } from '../../slices/headerSlice.mjs';
import { fetchForYouPreferences } from '../../slices/forYouSlice.mjs';
import { getAuthorizedReturnUrl } from '../../utils/auth/returnUrlValidator.mjs';
import {
    setPageTitle,
    setPageDescription,
    resetPageMetadata,
} from '../../slices/pageMetadataSlice.mjs';

import useIsMounted from '../../hooks/useIsMounted.mjs';

// Get token from session storage key named wsConnectionToken, if it exists.
const getSocketToken = () => {
    const token = sessionStorage.getItem('wsConnectionToken');
    return token ? token : null;
};

// Component for client-side navigation functionality
// Only rendered on the client after hydration
const ClientSideLoginHandler = ({ isAuthenticated, user }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();

    useEffect(() => {
        if (isAuthenticated && user) {
            // Apply user's theme preferences before navigating
            const preferences = user.preferences || {};

            // Apply color scheme (light/dark mode) - use explicit add/remove for mobile compatibility
            if (preferences.colorScheme === 'dark') {
                document.documentElement.classList.add('dark');
                localStorage.setItem('displayMode', 'dark');
            } else if (preferences.colorScheme === 'light') {
                document.documentElement.classList.remove('dark');
                localStorage.setItem('displayMode', 'light');
            }
            // If colorScheme is null/undefined, don't change - let localStorage/system preference apply

            // Apply selected theme
            if (preferences.selectedTheme && preferences.selectedTheme !== 'default') {
                document.documentElement.setAttribute('data-theme', preferences.selectedTheme);
                if (preferences.selectedThemeVersion) {
                    document.documentElement.setAttribute('data-theme-version', preferences.selectedThemeVersion);
                }
            } else {
                // Clear theme if default or null
                document.documentElement.removeAttribute('data-theme');
                document.documentElement.removeAttribute('data-theme-version');
            }

            // Fetch ForYou preferences so the selector derives nav children
            // (fire-and-forget — selectNavigationWithForYou derives nav on forYou state change)
            dispatch(fetchForYouPreferences());

            // Navigate to returnTo destination (validated) or home
            const returnTo = searchParams.get('returnTo');
            navigate(getAuthorizedReturnUrl(returnTo, user));
        }
    }, [isAuthenticated, user, navigate, searchParams]);

    return null; // This component doesn't render anything
};

// Create a separate component for the login content that will be wrapped by App
const LoginContent = () => {
    const [formData, setFormData] = useState({
        email: '',
        password: '',
    });
    const [validationErrors, setValidationErrors] = useState({});

    // Use the custom hook instead of local state
    const isMounted = useIsMounted();

    const { isAuthenticated, loading, error, errorCode, fieldErrors, user, resendLoading, resendSuccess, resendError } = useSelector(
        (state) => state.auth || {}
    );
    const dispatch = useDispatch();

    // Add refs for fields
    const emailRef = useRef(null);
    const passwordRef = useRef(null);

    // Clear errors when component unmounts
    useEffect(() => {
        dispatch(setPageTitle('Login - Nexus of Truth'));
        dispatch(setPageDescription('Nexus of Truth Login.'));
        dispatch(
            setHeaderData({
                title: 'Login',
                description: 'Sign in to Nexus of Truth.',
            })
        );
        return () => {
            if (error) {
                dispatch(clearError());
            }
            dispatch(clearResendState());
        };
    }, [dispatch, error]);

    // Handle API errors
    useEffect(() => {
        if (error) {
            // Use fieldErrors from Redux directly if available
            if (fieldErrors) {
                setValidationErrors(fieldErrors);
            }
            // Fallback to legacy error message handling
            else if (error.includes('Incorrect email or password')) {
                setValidationErrors({
                    email: 'Authentication failed',
                    password: 'Authentication failed',
                });
            }

            // Focus the appropriate field based on which has an error
            if (validationErrors.email) emailRef.current?.focus();
            else if (validationErrors.password) passwordRef.current?.focus();
        }
    }, [error, fieldErrors]);
    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData({
            ...formData,
            [name]: value,
        });

        // Clear error when user starts typing
        if (validationErrors[name]) {
            setValidationErrors({
                ...validationErrors,
                [name]: '',
            });
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.email) {
            newErrors.email = 'Email is required';
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
            newErrors.email = 'Email is invalid';
        }

        if (!formData.password) {
            newErrors.password = 'Password is required';
        }

        setValidationErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) return;

        const connectionToken = getSocketToken();

        dispatch(
            login({
                ...formData,
                connectionToken,
            })
        );
    };

    const handleResendVerification = () => {
        if (formData.email) {
            dispatch(resendVerificationEmail(formData.email));
        }
    };

    const isNotVerified = errorCode === 'EMAIL_NOT_VERIFIED';

    return (
        <>
            {/* Only render the navigation handler on client side */}
            {isMounted && <ClientSideLoginHandler isAuthenticated={isAuthenticated} user={user} />}

            <div className="auth-container">
                <div className="auth-form-wrapper">
                    <h1>Log In</h1>

                    {isNotVerified ? (
                        <div className="info-message">
                            <p>Your email has not been verified yet. Please check your inbox for a verification link.</p>
                            {resendSuccess ? (
                                <p className="success-text">Verification email sent! Please check your inbox.</p>
                            ) : (
                                <button
                                    type="button"
                                    className="resend-button"
                                    onClick={handleResendVerification}
                                    disabled={resendLoading}
                                >
                                    {resendLoading ? 'Sending...' : 'Resend verification email'}
                                </button>
                            )}
                            {resendError && <p className="error-text">{resendError}</p>}
                        </div>
                    ) : (
                        error && <div className="error-message">{error}</div>
                    )}

                    <form onSubmit={handleSubmit}>
                        <div className="form-group">
                            <label htmlFor="email">Email</label>
                            <input
                                ref={emailRef}
                                type="email"
                                id="email"
                                name="email"
                                value={formData.email}
                                onChange={handleChange}
                                className={validationErrors.email ? 'error' : ''}
                            />
                            {validationErrors.email && (
                                <div className="error-text">{validationErrors.email}</div>
                            )}
                        </div>

                        <div className="form-group">
                            <label htmlFor="password">Password</label>
                            <input
                                ref={passwordRef}
                                type="password"
                                id="password"
                                name="password"
                                value={formData.password}
                                onChange={handleChange}
                                className={validationErrors.password ? 'error' : ''}
                            />
                            {validationErrors.password && (
                                <div className="error-text">{validationErrors.password}</div>
                            )}
                        </div>

                        <button type="submit" className="submit-button" disabled={loading}>
                            {loading ? 'Logging in...' : 'Log In'}
                        </button>
                    </form>

                    <div className="auth-links">
                        {isMounted ? (
                            <>
                                <Link to={`/forgot-password${window.location.search}`}>Forgot Password?</Link>
                                <Link to={`/signup${window.location.search}`}>Don't have an account? Sign Up</Link>
                            </>
                        ) : (
                            <>
                                <a href="/forgot-password">Forgot Password?</a>
                                <a href="/signup">Don't have an account? Sign Up</a>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};

// Export the pure content; <App> is mounted by hydrate.js / SSR controllers
export default LoginContent;
